/**
 * OwnerTopUpModal — modal untuk owner initiate self-service top-up via Duitku.
 * Custom amount only (no preset), no min/max limits (as user requested).
 * On submit → POST /api/owner/topup/create → redirect to Duitku payment_url.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { CreditCard, X, Loader2, Wallet, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OwnerTopUpModal = ({ isOpen, onClose, ownerName, currentBalance }) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const parseAmount = (v) => {
    const cleaned = String(v || '').replace(/[^\d]/g, '');
    return cleaned ? parseInt(cleaned, 10) : 0;
  };

  const displayAmount = amount ? parseAmount(amount).toLocaleString('id-ID') : '';

  const handleSubmit = async () => {
    const amt = parseAmount(amount);
    if (amt <= 0) {
      setError('Masukkan jumlah top up terlebih dahulu');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/owner/topup/create`,
        { amount: amt },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const paymentUrl = res.data?.data?.payment_url;
      if (!paymentUrl) throw new Error(res.data?.detail || 'Gagal mendapat link pembayaran');
      // Redirect to Duitku payment page (owner picks channel there)
      window.location.href = paymentUrl;
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Gagal membuat top up');
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={loading ? undefined : onClose}
      data-testid="owner-topup-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-indigo-600 to-purple-600 p-5 text-white">
          <button
            onClick={onClose}
            disabled={loading}
            className="absolute right-3 top-3 rounded-full p-1 hover:bg-white/20 transition-colors disabled:opacity-50"
            data-testid="owner-topup-close-btn"
          >
            <X size={18} />
          </button>
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-white/20 p-2.5">
              <Wallet size={22} />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Top Up Saldo</h3>
              <p className="text-xs text-white/80">{ownerName || 'Owner'}</p>
            </div>
          </div>
          {typeof currentBalance === 'number' && (
            <div className="mt-3 rounded-lg bg-white/15 px-3 py-2 text-xs">
              Saldo saat ini: <span className="font-semibold">Rp {currentBalance.toLocaleString('id-ID')}</span>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Jumlah Top Up
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">Rp</span>
              <input
                type="text"
                inputMode="numeric"
                value={displayAmount}
                onChange={(e) => setAmount(parseAmount(e.target.value).toString())}
                placeholder="0"
                disabled={loading}
                autoFocus
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-lg font-semibold text-right text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                data-testid="owner-topup-amount-input"
              />
            </div>
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              Masukkan jumlah bebas — tidak ada batas minimum atau maksimum.
            </p>
          </div>

          {amount && parseAmount(amount) > 0 && (
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 p-3">
              <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-200 text-sm">
                <CreditCard size={16} />
                <span>Saldo Anda akan bertambah <strong>Rp {parseAmount(amount).toLocaleString('id-ID')}</strong> setelah pembayaran sukses.</span>
              </div>
              <p className="mt-1 text-[11px] text-emerald-700 dark:text-emerald-300">
                Fee gateway ditanggung perusahaan — Anda terima FULL sesuai jumlah top up.
              </p>
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 p-3 flex items-start gap-2" data-testid="owner-topup-error">
              <AlertCircle size={16} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading || !amount || parseAmount(amount) <= 0}
            className="w-full py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
            data-testid="owner-topup-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Memproses...
              </>
            ) : (
              <>
                <CreditCard size={16} /> Bayar via Duitku
              </>
            )}
          </button>

          <p className="text-[11px] text-center text-gray-500 dark:text-gray-400">
            Anda akan diarahkan ke halaman Duitku untuk memilih metode pembayaran (VA, QRIS, e-wallet, dll).
          </p>
        </div>
      </div>
    </div>
  );
};

export default OwnerTopUpModal;
